import React, { useRef } from 'react';
import { IconButton, Link, LinkProps, Modal, styled, Theme, Toolbar, useMediaQuery } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useNavigatorOnLine } from '../../hooks/useNavigatorOnline';
import { useLocation } from 'react-router-dom';
import { useServiceProvider } from '../../Providers';

const StyledModal = styled(Modal)(({ theme }) => ({
  background: 'rgba(0, 0, 0, 0.8)',
}));

const VideoIframe = styled('iframe')({
  width: '100%',
  aspectRatio: '16 / 9',
  border: 'none',
});

const ModalContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const VideoWrapper = styled('div')({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
}));

interface IProps extends LinkProps {
  videoId: string;
  timestampInSec?: number;
}

export const YoutubeReadyLink = ({videoId, timestampInSec, ...props}: IProps) => {
  const { navigationService } = useServiceProvider();

  const location = useLocation<{ videoId?: string }>();
  const open = location.state?.videoId === videoId;

  // prevent rendering youtube iframe until user clicks the link (to prevent cookies)
  const clickedOnce = useRef(false);

  const isMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const online = useNavigatorOnLine();

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (videoId) {
      clickedOnce.current = true;
      event.preventDefault();
      navigationService.navigate(location.pathname, { state: { videoId } });
    }
  };

  const handleClose = () => {
    navigationService.goBack();
  };

  const buildEmbeddedLink = () => {
    const url = `https://www.youtube.com/embed/${videoId}`;
    return timestampInSec ? `${url}?start=${timestampInSec}` : url;
  }

  const handleYoutube = online && isMdDown;

  return (
    <>
      {handleYoutube ? <Link {...props} onClick={handleClick} /> : <Link {...props} />}
      {handleYoutube && (
        <StyledModal open={open} onClose={handleClose}>
          <ModalContent>
            <Toolbar sx={{ justifyContent: 'flex-end' }}>
              <StyledIconButton onClick={handleClose}>
                <CloseOutlined />
              </StyledIconButton>
            </Toolbar>
            <VideoWrapper>
              {clickedOnce.current && (
                <VideoIframe
                  src={buildEmbeddedLink()}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </VideoWrapper>
          </ModalContent>
        </StyledModal>
      )}
    </>
  );
};
